// Feature C — "Explain this chart" visual-context builders.
//
// Each builder maps one of the Executive Command Center's already-computed,
// already-masked on-page arrays into the `visual` payload attached to /ai/ask:
//
//   { title, kind, unit, series:[{ label, value, secondary? }], axes?, note? }
//
// The plotted arrays passed in are the EXACT values the charts render (post
// role-masking, which the page applies before charting). Only real, finite
// numbers survive into `series`, so a restricted role's snapshot never leaks a
// figure the page itself hides — the copilot can only explain what is on screen.

function finiteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function withNote(payload, note) {
  return note ? { ...payload, note } : payload;
}

// InvestmentDonut — `investmentSlices` = [{ id?, name|label, value }] in USD M.
export function buildDonutVisual(slices = [], { note } = {}) {
  const series = (Array.isArray(slices) ? slices : [])
    .map((slice) => {
      const value = finiteNumber(slice?.value);
      if (value === null) return null;
      return { label: String(slice?.name ?? slice?.label ?? 'Unlabeled'), value };
    })
    .filter(Boolean);

  return withNote(
    {
      title: 'Investment by portfolio',
      kind: 'donut',
      unit: 'USD M',
      series,
    },
    note,
  );
}

// MissionImpactScatter — `scatter` = [{ id, name, spend, impact, ... }].
// Two dimensions collapse to one value per row: value = mission impact,
// secondary = spend, so the fact block stays 1-value-per-row and unambiguous.
export function buildScatterVisual(points = [], { note } = {}) {
  const series = (Array.isArray(points) ? points : [])
    .map((point) => {
      const value = finiteNumber(point?.impact);
      if (value === null) return null;
      const spend = finiteNumber(point?.spend);
      const row = { label: String(point?.name ?? point?.label ?? 'Unlabeled'), value };
      if (spend !== null) row.secondary = `${spend} USD M spend`;
      return row;
    })
    .filter(Boolean);

  return withNote(
    {
      title: 'Mission impact vs spend',
      kind: 'scatter',
      unit: 'score',
      series,
      axes: { x: 'Spend (USD M)', y: 'Mission impact' },
    },
    note,
  );
}

// StatusDonutMini — `healthDonutSegments` = [{ label, count, tone }].
export function buildHealthVisual(segments = [], { note } = {}) {
  const series = (Array.isArray(segments) ? segments : [])
    .map((segment) => {
      const value = finiteNumber(segment?.count);
      if (value === null) return null;
      return { label: String(segment?.label ?? 'Unlabeled'), value };
    })
    .filter(Boolean);

  return withNote(
    {
      title: 'Project health distribution',
      kind: 'donut',
      unit: 'count',
      series,
    },
    note,
  );
}
